import React, {FC} from 'react';

import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DatePicker} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';

import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/ca';
import 'dayjs/locale/en';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {SxProps} from '@mui/material';


//TYPES
export type Props = {
  label: string,
  required: boolean,
  readOnly: boolean,
  disableToday?: boolean,
  value: Dayjs | undefined,
  maxDate?: Dayjs,
  minDate?: Dayjs,
  onChange: (newValue?: Dayjs) => void
};

const CalendarPicker: FC<Props> = ({label, required, readOnly, disableToday=false, value, maxDate, minDate, onChange}) => {

  const {t} = useTranslation();
  const lang = i18n.resolvedLanguage;

  return <FormControl fullWidth required={required}>
    <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{todayButtonLabel: t('requestImages.today')}} adapterLocale={lang}>
      <Stack direction="column" sx={styles.container}>
        <Stack direction="row" sx={styles.header}>
          <CalendarTodayIcon sx={readOnly ? styles.headerIconReadOnly : styles.headerIcon}/>
          <Typography sx={readOnly ? styles.headerLabelReadOnly : styles.headerLabel}>{label} {required && '*'}</Typography>
        </Stack>
        <DatePicker
          format="DD/MM/YYYY"
          maxDate={maxDate || today}
          minDate={minDate}
          value={value}
          readOnly={readOnly}
          slotProps={{
            popper: {sx: {...styles.datePickerPopper},},
            layout: {sx: {...styles.datePickerLayout}},
            actionBar: {
              actions: disableToday ? [] : ['today'],
              sx: {...styles.datePickerActionBar}
            },
            textField: {
              size: 'small',
              InputLabelProps: {sx: {...styles.datePickerTextField}}
            },
            field: { clearable: false },
          }}
          sx={styles.calendar}
          onChange={(day: Dayjs | null) => onChange(day == null ? undefined : day)}
        />
      </Stack>
    </LocalizationProvider>
  </FormControl>;
};
export default CalendarPicker;

const today = dayjs();

const styles: Record<string, SxProps> = {
  container: {
    gap: 1
  },
  header: {
    alignItems: 'center', 
    gap: 1
  },
  headerIcon: {
    fontSize: '20px', 
    color: 'primary.main'
  },
  headerIconReadOnly: {
    fontSize: '20px',
    color: 'grey.400'
  },
  headerLabel: {
    ml: 0.5,
    color: 'text.primary'
  },
  headerLabelReadOnly: {
    ml: 0.5,
    color: 'grey.400'
  },
  datePickerPopper: {
    opacity: 0.95
  },
  datePickerLayout: {
    '& .MuiPaper-root': { opacity: 0.5 }
  },
  datePickerActionBar: {
    justifyContent: 'center',
    '& .MuiButtonBase-root': {
      width: '100px',
      border: 1,
      borderColor: 'primary.main',
      mb: 1
    }
  },
  datePickerTextField: {
    '& .MuiOutlinedInput-notchedOutline': {
      display: 'none'
    }
  },
  calendar: {
    flex: 1,
    backgroundColor: 'grey.100'
  }
};